import { reject } from "../TypingUtils";
import { Fetch } from "../HttpClient/FetchImplementation/Fetch";
import { ExceptionLogger, getContentType, getFileName, HttpClient, HttpError } from "../HttpClient/HttpClient";
import { HttpStatusCode, HttpStatusHandlers, IHttpClient } from "../HttpClient/IHttpClient";
import { AuthenticationMode, createAuthenticationProvider } from "../HttpClient/AuthenticationMode";
import { ICombineByTransfers } from "../../Engine/src/Engine/Core/ModalManager/Modals/UploadSection/Types";
import { AbsoluteModelPath, convertToLegacyPath } from "../ModelPath/ModelPath";
import { convertDateFromStringIfNeeded } from "../TypeConversionHelper";
import { AUOOptions } from "../../Engine/src/Forms/Types/AUOOptions";
import { WorkspaceResourceType } from "../../Engine/src/ContractorsServiceIntegration/Types/WorkspaceTypes";

import {
    CheckScenarioType,
    ConvertedPDFMetaResponse,
    ConvertedPdfResponse,
    DraftSettings,
    FufWithAttachmentsResponse,
    FufWithAttachmentsTaskResult,
    GetPartialFufResponse,
    GetUserResponse,
    ICandyApiClient,
    ICandyApiDraftClient,
    IInnerChangeResult,
    IInnerChangeSuccessResult,
    NormalizationProgressTask,
    PathLoadSpecification,
    RequestNdsFileOptions,
} from "./ICandyApiClient";
import { ServerErrorLog } from "./Types/ErrorLog/ServerErrorLog";
import { ServerInnerResponse, ServerJsonResponse } from "./Types/GetInner/ServerInnerResponse";
import {
    ExcelsToPartialFufsResponse,
    FileProperties,
    ServerErrorResponse,
    UploadFilePropertiesResponse,
} from "./Types/DraftActions/FileProperties";
import { CreateDraftResponse } from "./Types/CreateDraft/CreateDraftResponse";
import { CreateExplanatoryNoteResponse } from "./Types/ExplanatoryNote/CreateExplanatoryNoteResponse";
import { DraftFullId } from "./Types/DraftFullId";
import { ServerChangeSet } from "./Types/ChangeSet/ServerChangeSet";
import { ServerTask, VerifyAndFixFnsRequirementsTaskInfo } from "./Types/Tasks/Tasks";
import { FocusServerErrorCode, GetByInnResponse } from "./Types/Focus/FocusTypes";
import { AttachInfo } from "./Types/Attachments/Attacments";
import { DraftPresentationType } from "./Types/Presentations/DraftPresentationType";
import { PresentationContent } from "./Types/Presentations/PresentationContent";
import { ConvertDraftRequest } from "./Types/DraftActions/ConvertDraftRequest";
import { GetAttachmentsResponse } from "./Types/Attachments/GetAttachmentsResponse";
import { FocusServerResponse } from "./Types/Focus/FocusServerResponse";
import { GetTasksServerResponse } from "./Types/Tasks/GetTasksServerResponse";
import { ServerRequisitesResponse } from "./Types/GetInner/ServerRequisitesResponse";
import { isBuffer } from "./IsBuffer";
import { CombinatorType, CombineFufResult, FufCombineMethod } from "./Types/DraftActions/CombineFufResult";
import { FeatureFlagsResponse } from "./Types/FeatureFlags/FeatureFlagsResponse";
import { FufSizeTooLargeError } from "./Types/DraftActions/FufSizeTooLargeError";
import { ImportEmployeesRequest } from "./Types/Staff/ImportEmployeesRequest";
import { MassActionParameters } from "./Types/MassActions/MassActionParameters";
import { PrintType } from "./Types/Print/PrintType";
import { PrintResponse } from "./Types/Print/PrintResponse";
import { ServerRawResponse } from "./Types/GetInner/ServerRawResponse";
import { CandyPopdProtocol } from "./Types/ErrorLog/CandyPopdProtocol";
import { InstancesDeletingRule } from "./Types/InstancesDeleting/InstancesDeletingRule";
import { ValueForInstancesSettingRule } from "./Types/MassActions/ValueForInstancesSettingRule";
import { InnerQueryRequest } from "./Types/QueryCollecton/InnerQueryRequest";
import { InnerQueryResponse } from "./Types/QueryCollecton/InnerQueryResponse";
import { ResolveInnerPathRequest } from "./Types/ErrorLog/ResolveInnerPathRequest";
import { ResolvePathResponse } from "./Types/ErrorLog/ResolvePathResponse";
import {
    InnerHistoryResponse,
    InnerHistoryUndoResponse,
    InnerHistoryUndoResponseWithStatus,
} from "./Types/HistoryEditing/InnerHistoryResponse";
import { GFVResponse } from "./Types/GFV/GFVResponse";
import { NeedNormalizationError } from "./Types/DraftActions/NeedNormalizationError";
import { PollingTaskWithResult } from "./Types/PollingTasks/PollingTasks";
import { waitPollingTask } from "./Types/PollingTasks/PollingTaskWaiter";
import { ConvertationError, ServerError, ErrorCode, ErrorResponse } from "./CandyApiErrors";

const strangeOptions = { src: "changeSet", dataVersion: "0" };

export const CandyResourceZones = {
    TestForms: "candy.resources.test-forms",
};

function convertPathLoadSpecificationToString(spec: PathLoadSpecification): string {
    return (
        (spec.skip ? "-" : "") +
        (spec.pathOrPathPrefix === "*" ? spec.pathOrPathPrefix : spec.pathOrPathPrefix.toLegacyPath())
    );
}

export class CandyApiClient implements ICandyApiClient {
    private readonly httpClient: IHttpClient;

    public constructor(
        fetchImpl: Fetch,
        baseUrl: string,
        resourceZone: undefined | string,
        authentication: undefined | AuthenticationMode,
        exceptionLogger?: ExceptionLogger,
        httpStatusHandlers?: HttpStatusHandlers
    ) {
        this.httpClient = new HttpClient(
            fetchImpl,
            baseUrl,
            createAuthenticationProvider(authentication),
            {
                resourceZone: resourceZone,
            },
            exceptionLogger,
            httpStatusHandlers
        );
    }

    public forDraft(draftFullId: DraftFullId): ICandyApiDraftClient {
        return new CandyApiDraftClient(this.httpClient, draftFullId);
    }

    public async getInfoByInns(draftFullId: DraftFullId, inns: readonly string[]): Promise<GetByInnResponse> {
        const paths = inns.map(inn => `@focus/${inn}`);
        const response = await this.httpClient
            .post(this.getDraftUrl(draftFullId, "get-inner"))
            .withJsonBody(paths)
            .json<FocusServerResponse>();
        const result: GetByInnResponse = {};
        for (const inn of inns) {
            const innServerValue = response[`@focus/${inn}`]?.value;
            if (innServerValue == undefined) {
                result[inn] = {
                    value: undefined,
                    error: "Server does not return inn info",
                    errorCode: FocusServerErrorCode.NoInnError,
                };
            } else {
                result[inn] = {
                    value: innServerValue.value ?? undefined,
                    errorCode: innServerValue.errorCode ?? undefined,
                    error: innServerValue.error ?? undefined,
                };
            }
        }
        return result;
    }

    public async getAllGfvs(): Promise<string[]> {
        return this.httpClient.get(`build-server/gfvs`).json<string[]>();
    }

    public async forceGetAllGfvs(): Promise<string[]> {
        return this.httpClient.get(`build-server/force-gfvs`).json<string[]>();
    }

    public async createDraftFromDrive(
        ns: string,
        fufDriveUri: string,
        abortSignal?: AbortSignal
    ): Promise<CreateDraftResponse> {
        return await this.httpClient
            .post(`v1/ns/${ns}/create-draft-from-drive`)
            .withQueryParams({
                ns: ns,
                fufDriveUri: fufDriveUri,
            })
            .withAbortSignal(abortSignal)
            .json<CreateDraftResponse>();
    }

    public async createEmptyDraft(ns: string, gfv: string, abortSignal?: AbortSignal): Promise<CreateDraftResponse> {
        return await this.httpClient
            .post(`v1/ns/${ns}/create-draft`)
            .withQueryParams({ gfv: gfv })
            .withAbortSignal(abortSignal)
            .json<CreateDraftResponse>();
    }

    public createDraft(ns: string, gfv: string): Promise<CreateDraftResponse>;
    public createDraft(ns: string, gfv: undefined, fufContent: Buffer): Promise<CreateDraftResponse>;
    public async createDraft(ns: string, gfv: undefined | string, fufContent?: Buffer): Promise<CreateDraftResponse> {
        if (gfv == undefined && fufContent != undefined) {
            return await this.httpClient
                .post(`v1/ns/${ns}/create-draft`)
                .withContentType("application/octet-stream")
                .withBodyFromBuffer(fufContent)
                .json<CreateDraftResponse>();
        }
        if (gfv != undefined && fufContent == undefined) {
            return await this.httpClient
                .post(`v1/ns/${ns}/create-draft`)
                .withQueryParams({ gfv: gfv })
                .withContentType("application/octet-stream")
                .withBodyFromBuffer(Buffer.from([]))
                .json<CreateDraftResponse>();
        }
        if (gfv == undefined && fufContent == undefined) {
            throw new Error("Cannot create draft: no gfv and no fuf specified");
        }
        return await this.httpClient
            .post(`v1/ns/${ns}/create-draft`)
            .withQueryParams({ gfv: gfv })
            .withContentType("application/octet-stream")
            .withBodyFromBuffer(fufContent)
            .json<CreateDraftResponse>();
    }

    public async checkAlive(): Promise<void> {
        await this.httpClient.get(`_status/ping`);
    }

    private getDraftUrl(draftFullId: DraftFullId, draftRelatedUrl: string): string {
        return `v1/ns/${draftFullId.ns}/drafts/${draftFullId.draftId}/${draftRelatedUrl}`;
    }

    public async getFeatureFlags(abonId?: string, orgId?: string, userId?: string): Promise<FeatureFlagsResponse> {
        return await this.httpClient
            .get("v1/features/flags")
            .withQueryParams({
                abon: abonId,
                org: orgId,
                user: userId,
            })
            .json<FeatureFlagsResponse>();
    }

    public async getWorkspaceDirectoryId(
        orgId: string,
        inn: string,
        resourceType: WorkspaceResourceType,
        kpp?: string
    ): Promise<string> {
        return await this.httpClient
            .get("v1/workspace/directoryId")
            .withQueryParams({
                organizationId: orgId,
                inn: inn,
                kpp: kpp,
                resourceType: resourceType,
            })
            .text();
    }

    public async convertToPdfAndGetMeta(driveUri: string): Promise<ConvertedPDFMetaResponse> {
        return this.convertFile<ConvertedPDFMetaResponse>("convert-to-pdf-and-get-meta", driveUri);
    }

    public async convertToPdf(driveUri: string): Promise<ConvertedPdfResponse> {
        return this.convertFile<ConvertedPdfResponse>("convert-to-pdf", driveUri);
    }

    private async convertFile<T>(endpoint: string, driveUri: string): Promise<T> {
        const url = `v1/files/${endpoint}`;

        try {
            return await this.httpClient.get(url).withQueryParams({ driveUri: driveUri }).json<T>();
        } catch (e) {
            if (e instanceof HttpError) {
                if (e.statusCode === 400) {
                    throw new ConvertationError(e.message);
                }
                throw new ServerError(e.message);
            }
            throw new ServerError(`Ошибка сервера в ответ на url: ${url}`);
        }
    }
}

export class CandyApiDraftClient implements ICandyApiDraftClient {
    private readonly httpClient: IHttpClient;
    private readonly draftFullId: DraftFullId;

    private readonly normalizeDownloadLink = (uri: string): string =>
        uri.includes("response-content-disposition") ? uri : `${uri}?response-content-disposition=attachment`;

    public constructor(httpClient: IHttpClient, draftFullId: DraftFullId) {
        this.httpClient = httpClient;
        this.draftFullId = draftFullId;
    }

    public getHrefToInnerEditor(): string {
        return this.httpClient.url(`inner-editor/ns/${this.draftFullId.ns}/drafts/${this.draftFullId.draftId}`);
    }

    public getHrefToConvertationLog(): string {
        return this.httpClient.url(this.getDraftUrl("conversion"));
    }

    public async getInnerFuf(): Promise<string | undefined> {
        return (await this.httpClient.get(this.getDraftUrl(DraftPresentationType.innerFuf))).text();
    }

    public async startGetInnerFufTask(): Promise<PollingTaskWithResult<string>> {
        return await this.httpClient
            .post(this.getDraftUrl(DraftPresentationType.innerFuf + "/get-task"))
            .json<PollingTaskWithResult<string>>();
    }

    public async getInnerFufTask(): Promise<PollingTaskWithResult<string>> {
        return await this.httpClient
            .get(this.getDraftUrl(DraftPresentationType.innerFuf + "/get-task"))
            .json<PollingTaskWithResult<string>>();
    }

    public getHrefToDownloadInnerFuf(): string {
        return this.getHrefDownloadDraftPresentation(DraftPresentationType.innerFuf);
    }

    public getHrefToDownloadClientInfo(): string {
        return this.getHrefDownloadDraftPresentation(DraftPresentationType.clientInfo);
    }

    public getHrefToDownloadFormsClientInfo(): string {
        return this.getHrefDownloadDraftPresentation(DraftPresentationType.formsClientInfo);
    }

    public async getErrorLog(): Promise<ServerErrorLog> {
        return this.httpClient.get(this.getDraftPresentationUrl(DraftPresentationType.errorLog)).json<ServerErrorLog>();
    }

    public async getErrorLogWithCheckScenarios(checkScenarios: CheckScenarioType[]): Promise<ServerErrorLog> {
        return this.httpClient
            .post(this.getDraftUrl("errorlog"))
            .withJsonBody({ checkScenarios: checkScenarios })
            .json<ServerErrorLog>();
    }

    public async getTaskById(taskId: string): Promise<ServerTask | undefined> {
        const tasks = await this.httpClient
            .post(this.getDraftUrl("get-inner"))
            .withJsonBody([`@task/*`])
            .json<GetTasksServerResponse>();
        return tasks[`@task/${taskId}`]?.value;
    }

    public async getFufWithAttachments(): Promise<FufWithAttachmentsResponse | undefined> {
        const response = await this.httpClient
            .get(this.getDraftUrl("get-fuf-with-attachments"))
            .json<FufWithAttachmentsResponse>();
        return response;
    }

    public async startGetFufWithAttachmentsTask(): Promise<PollingTaskWithResult<FufWithAttachmentsTaskResult>> {
        const response = await this.httpClient
            .post(this.getDraftUrl("get-fuf-with-attachments-task"))
            .json<PollingTaskWithResult<FufWithAttachmentsTaskResult>>();
        return response;
    }

    public async getFufWithAttachmentsTask(
        format: "Json" | "DriveUri"
    ): Promise<PollingTaskWithResult<FufWithAttachmentsTaskResult>> {
        const response = await this.httpClient
            .get(this.getDraftUrl("get-fuf-with-attachments-task"))
            .withQueryParams({ format: format })
            .json<PollingTaskWithResult<FufWithAttachmentsTaskResult>>();
        return response;
    }

    public async getPartialFuf(rules: string[]): Promise<GetPartialFufResponse | undefined> {
        const response = await this.httpClient
            .post(this.getDraftUrl("get-partial-fuf"))
            .withJsonBody(rules)
            .json<GetPartialFufResponse>();
        return response;
    }

    public async getAllTasks(): Promise<ServerTask[]> {
        const tasks = await this.httpClient
            .post(this.getDraftUrl("get-inner"))
            .withJsonBody([`@task/*`])
            .json<GetTasksServerResponse>();
        return Object.values(tasks).map(x => x.value);
    }

    public async downloadFuf(): Promise<void> {
        await this.startGetFufWithAttachmentsTask();
        const response = await waitPollingTask(() => this.getFufWithAttachmentsTask("DriveUri"));
        if (!("resultUri" in response) || !response.resultUri) {
            reject("Failed to receive FUF link");
        }
        window.location.href = this.normalizeDownloadLink(response.resultUri);
    }

    public async makeDownloadDriveLink(uri: string, fileName?: string): Promise<void> {
        const url = this.makeDriveLink(uri, fileName);
        this.downloadByUri(url);
    }

    private makeDriveLink(uri: string, fileName?: string): string {
        if (fileName) {
            const contentDisposition = this.makeContentDispositionParam(fileName);
            return `${uri}?${contentDisposition}`;
        }
        return this.normalizeDownloadLink(uri);
    }

    // Сделано согласно доке (вкладка "Контент: скачивание"): https://wiki.skbkontur.ru/pages/viewpage.action?pageId=156699377
    private makeContentDispositionParam(fileName: string): string {
        const name = this.encodeRFC5987ValueChars(fileName);
        const contentDispositionValue = `attachment; filename*=UTF-8''${name}`;
        return `response-content-disposition=${this.encodeRFC5987ValueChars(contentDispositionValue)}`;
    }

    // Оригинал - https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/encodeURIComponent#encoding_for_content-disposition_and_link_headers
    private encodeRFC5987ValueChars(str: string) {
        return (
            encodeURIComponent(str)
                // The following creates the sequences %27 %28 %29 %2A (Note that
                // the valid encoding of "*" is %2A, which necessitates calling
                // toUpperCase() to properly encode). Although RFC3986 reserves "!",
                // RFC5987 does not, so we do not need to escape it.
                .replace(/['()*]/g, c => `%${c.charCodeAt(0).toString(16).toUpperCase()}`)
                // The following are not required for percent-encoding per RFC5987,
                // so we can allow for a little better readability over the wire: |`^
                .replace(/%(7C|60|5E)/g, (_str, hex: string) => String.fromCharCode(parseInt(hex, 16)))
        );
    }

    private downloadByUri(uri: string) {
        window.location.href = uri;
    }

    public async downloadInner(): Promise<void> {
        const response = await this.httpClient.get(this.getDraftPresentationUrl(DraftPresentationType.inner));
        const responseBlob = await response.blob();

        const innerFileBlob = new Blob([responseBlob], {
            type: getContentType(response) ?? "application/json",
        });
        const innerFileUrl = window.URL.createObjectURL(innerFileBlob);
        const link = document.createElement("a");
        link.href = innerFileUrl;
        link.download = getFileName(response) ?? "inner.json";
        link.click();

        link.remove();
        setTimeout(() => {
            window.URL.revokeObjectURL(innerFileUrl);
        }, 250);
    }

    public async enqueueTask(taskId: string, taskInfo: VerifyAndFixFnsRequirementsTaskInfo): Promise<void> {
        await this.httpClient
            .post(this.getDraftPresentationUrl(DraftPresentationType.inner))
            .withQueryParams(strangeOptions)
            .withJsonBody({
                added: [],
                changed: {
                    [`@task/${taskId}`]: {
                        ...taskInfo,
                        id: taskId,
                    },
                },
                removed: [],
            });
    }

    public async cancelTask(taskId: string): Promise<void> {
        await this.httpClient
            .post(this.getDraftPresentationUrl(DraftPresentationType.inner))
            .withQueryParams(strangeOptions)
            .withJsonBody({
                added: [],
                changed: {},
                removed: [`@task/${taskId}`],
            });
    }

    public getExpertErrorLog(auo: Partial<AUOOptions>): Promise<ServerErrorLog> {
        return this.httpClient
            .post(this.getDraftUrl("error-log-expert"))
            .withJsonBody({ auo: auo })
            .json<ServerErrorLog>();
    }

    public getHrefToErrorLogPdf(errorLogName: string, propsName: string): string {
        return this.httpClient.url(this.getDraftUrl("presentations/errorLog/print"), {
            errorLogName: errorLogName,
            propsDriveName: propsName,
        });
    }

    public async clearDraft(): Promise<void> {
        await this.httpClient.post(this.getDraftUrl("clear"));
    }

    public async restoreToSourceFuf(): Promise<void> {
        await this.httpClient.post(this.getDraftUrl("restore-to-source-fuf"));
    }

    public async convertDraftFromPrevGfv({ prevGfvDraftId, gfv }: ConvertDraftRequest): Promise<void> {
        await this.httpClient.post(this.getDraftUrl("convert")).withQueryParams({
            id: prevGfvDraftId,
            gfv: gfv,
        });
    }

    public async sendChangeWithNormalizationSet(
        data: ServerChangeSet,
        currentDataVersion: number,
        normalizerId?: string,
        operationName?: string
    ): Promise<IInnerChangeResult> {
        const result = await this.httpClient
            .post(this.getDraftUrl("inner/patch-with-normalization"))
            .withQueryParams({
                dataVersion: currentDataVersion,
                normalizerId: normalizerId,
                operationName: operationName ?? undefined,
            })
            .withJsonBody(data)
            .acceptCode(409);

        return result.status == 200 ? await result.json<IInnerChangeSuccessResult>() : result.status;
    }

    public async startPatchWithNormalizationTask(
        data: ServerChangeSet,
        currentDataVersion: number,
        normalizerId?: string,
        operationName?: string
    ): Promise<PollingTaskWithResult<IInnerChangeSuccessResult> | number> {
        const result = await this.httpClient
            .post(this.getDraftUrl("inner/patch-with-normalization-task"))
            .withQueryParams({
                dataVersion: currentDataVersion,
                normalizerId: normalizerId,
                operationName: operationName,
            })
            .withJsonBody(data)
            .acceptCode(409);
        return result.status == 200
            ? await result.json<PollingTaskWithResult<IInnerChangeSuccessResult>>()
            : result.status;
    }

    public async getPatchWithNormalizationTask(): Promise<PollingTaskWithResult<IInnerChangeSuccessResult>> {
        const result = await this.httpClient.get(this.getDraftUrl("inner/patch-with-normalization-task"));
        return result.json<PollingTaskWithResult<IInnerChangeSuccessResult>>();
    }

    public async sendChangeSet(
        data: ServerChangeSet,
        currentDataVersion: number,
        operationName?: string
    ): Promise<IInnerChangeResult> {
        const result = await this.httpClient
            .post(this.getDraftUrl("inner"))
            .withQueryParams({ dataVersion: currentDataVersion, operationName: operationName ?? undefined })
            .withJsonBody(data)
            .acceptCode(409);

        const getSuccessResult = async () => {
            const dataResult = await result.json<{ dataVersion?: number }>();
            return {
                dataVersion: dataResult?.dataVersion ?? reject("Invalid dataVersion after applying change set."),
            };
        };

        return result.status == 200 ? await getSuccessResult() : result.status;
    }

    public sendChangeSetSync(data: ServerChangeSet, currentDataVersion: number): IInnerChangeResult {
        const result = this.httpClient.postSync(this.getDraftUrl("inner"), { dataVersion: currentDataVersion }, data);

        const getSuccessResultSync = () => {
            const dataResult = result.json<{ dataVersion?: number }>();
            return {
                dataVersion: dataResult?.dataVersion ?? reject("Invalid dataVersion after applying change set."),
            };
        };
        return result.status == 200 ? getSuccessResultSync() : result.status;
    }

    public async getRequisites(requisiteList: readonly string[]): Promise<ServerRequisitesResponse> {
        return await this.httpClient
            .post(this.getDraftUrl("get-inner"))
            .withJsonBody(requisiteList)
            .json<ServerRequisitesResponse>();
    }

    public async getInner(paths: undefined | readonly string[]): Promise<ServerInnerResponse> {
        return await this.httpClient
            .post(this.getDraftUrl("get-inner"))
            .withJsonBody(paths)
            .json<ServerInnerResponse>();
    }

    public getPartialInner(rules: PathLoadSpecification[], maxInstanceCount?: number): Promise<ServerInnerResponse> {
        const convertedRules = rules.map(convertPathLoadSpecificationToString);
        return this.httpClient
            .post(this.getDraftUrl("get-partial-inner"))
            .withQueryParams(maxInstanceCount != undefined && { maxInstanceCount: maxInstanceCount.toString() })
            .withJsonBody(convertedRules.length > 0 ? convertedRules : ["*"])
            .json<ServerInnerResponse>();
    }

    public async getPartialInnerAsJsonTree(
        rules: PathLoadSpecification[],
        maxInstanceCount?: number,
        skipNormalization?: boolean
    ): Promise<ServerJsonResponse> {
        const convertedRules = rules.map(convertPathLoadSpecificationToString);
        const response = await this.httpClient
            .post(this.getDraftUrl("get-partial-inner"))
            .withQueryParams(maxInstanceCount != undefined && { maxInstanceCount: maxInstanceCount.toString() })
            .withQueryParams({ format: "JsonTree", useDraftNormalization: !skipNormalization })
            .withJsonBody(convertedRules.length > 0 ? convertedRules : ["*"])
            .acceptCode(424);

        if (response.status === 424) {
            throw new NeedNormalizationError("get-partial-inner - Черновик не нормализован");
        }

        return response.json<ServerJsonResponse>();
    }

    public async getRawPartialInnerAsJsonTree(
        rules: readonly PathLoadSpecification[],
        maxInstanceCount?: number,
        skipNormalization?: boolean
    ): Promise<ServerRawResponse> {
        const convertedRules = rules.map(convertPathLoadSpecificationToString);
        const response = await this.httpClient
            .post(this.getDraftUrl("get-partial-inner2"))
            .withQueryParams(maxInstanceCount != undefined && { maxInstanceCount: maxInstanceCount.toString() })
            .withQueryParams({ useDraftNormalization: !skipNormalization })
            .withJsonBody(convertedRules.length > 0 ? convertedRules : ["*"])
            .acceptCode(424);

        if (response.status === 424) {
            throw new NeedNormalizationError("get-partial-inner2 - Черновик не нормализован");
        }

        return response.json<ServerRawResponse>();
    }

    public getDataVersion(): Promise<number> {
        return this.httpClient.get(this.getDraftUrl("inner/get-data-version")).json<number>();
    }

    public startNormalizationTask(): Promise<NormalizationProgressTask> {
        return this.httpClient.post(this.getDraftUrl("normalization-task")).json<NormalizationProgressTask>();
    }

    public getNormalizationTask(): Promise<NormalizationProgressTask> {
        return this.httpClient.get(this.getDraftUrl("normalization-task")).json<NormalizationProgressTask>();
    }

    public async getExplanatoryNote(): Promise<CreateExplanatoryNoteResponse> {
        return await this.httpClient
            .get(this.getDraftPresentationUrl(DraftPresentationType.explanatoryNote))
            .json<CreateExplanatoryNoteResponse>();
    }

    public async getAttachmentById(attachmentId: string): Promise<AttachInfo> {
        const result = await this.httpClient
            .post(this.getDraftUrl("get-inner"))
            .withJsonBody([`@attachment/${attachmentId}`])
            .json<GetAttachmentsResponse>();
        return result[`@attachment/${attachmentId}`]?.value ?? reject();
    }

    public async getAllAttachments(): Promise<AttachInfo[]> {
        const result = await this.httpClient
            .post(this.getDraftUrl("get-inner"))
            .withJsonBody([`@attachment/*`])
            .json<GetAttachmentsResponse>();
        return Object.values(result).map(x => x.value);
    }

    public async removeAttachments(attachmentIds: readonly string[]): Promise<void> {
        await this.httpClient
            .post(this.getDraftPresentationUrl(DraftPresentationType.inner))
            .withQueryParams(strangeOptions)
            .withJsonBody({
                added: [],
                changed: {},
                removed: attachmentIds.map(id => `@attachment/${id}`),
            });
    }

    public async createOrUpdateAttachments(attachments: readonly AttachInfo[]): Promise<void> {
        const changed: Record<string, {}> = {};
        for (const attachment of attachments) {
            changed[`@attachment/${attachment.id}`] = {
                contentUri: attachment.contentUri,
                id: attachment.id,
                meta: attachment.meta,
            };
        }
        await this.httpClient
            .post(this.getDraftPresentationUrl(DraftPresentationType.inner))
            .withQueryParams(strangeOptions)
            .withJsonBody({
                added: [],
                changed: changed,
                removed: [],
            });
    }

    public async createDraftAttachment(gfv: string, path: string): Promise<AttachInfo> {
        return await this.httpClient
            .post(this.getDraftUrl("external/create-attachment-draft"))
            .withQueryParams({ gfv: gfv, path: path })
            .json<AttachInfo>();
    }

    public async openLoadedAttachmentAsDraft(id: string): Promise<AttachInfo> {
        const response = await this.httpClient
            .post(this.getDraftUrl("external/open-loaded-attachment-as-draft"))
            .withQueryParams({ attachmentId: id })
            .acceptCode(413);

        if (response.status === 413) {
            const errorData = await response.json<ErrorResponse>();
            if (errorData.errorCode === ErrorCode.FufSizeTooLarge) {
                throw new FufSizeTooLargeError(errorData.message);
            }
            throw new HttpError(errorData.message, response.status);
        }
        return await response.json<AttachInfo>();
    }

    public async setPresentation(presentationType: DraftPresentationType, content: string | unknown): Promise<void> {
        let request = this.httpClient.put(this.getDraftPresentationUrl(presentationType));
        if (typeof content === "string") {
            request = request.withRawBody(content);
        } else if (isBuffer(content)) {
            request = request.withRawBody(content);
        } else {
            request = request.withJsonBody(content);
        }
        await request;
    }

    public async patchPresentation(
        presentationType: DraftPresentationType,
        presentationContent: PresentationContent
    ): Promise<void> {
        await this.httpClient.patch(this.getDraftPresentationUrl(presentationType)).withJsonBody(presentationContent);
    }

    public async getFileProperties(driveUri: string, ignoreSizeCheck?: boolean): Promise<FileProperties | undefined> {
        const response = await this.httpClient
            .get(`v1/files/get-properties`)
            .withQueryParams({ driveUri: driveUri, ignoreSizeCheck: ignoreSizeCheck ?? false })
            .acceptCode(400)
            .acceptCode(413);
        if (response.status === 400 || response.status === 413 || response.status === 200) {
            return response.json<FileProperties>();
        }
        return undefined;
    }

    public async getFileNdsProperties({
        driveUri,
        declarationGfv,
        requestedGfv,
        ignoreSizeCheck,
    }: RequestNdsFileOptions): Promise<FileProperties[] | undefined> {
        const taskResponse = await this.httpClient.post(`v1/files/put-get-properties-nds-task-v2`).withJsonBody({
            driveUri: driveUri,
            ignoreSizeCheck: ignoreSizeCheck ?? false,
            declarationGfv: declarationGfv,
            requiredGfv: requestedGfv,
        });

        const taskResult = await taskResponse.json<{ id: string }>();

        if (!taskResult?.id) {
            return undefined;
        }

        const result = await waitPollingTask<FileProperties[]>(() =>
            this.httpClient.get("v1/files/get-properties-nds-task-v2").withQueryParams({ taskId: taskResult.id }).json()
        );

        if (result) {
            return result;
        }
        return undefined;
    }

    public async prepareAndGetFileProperties(
        driveUri: string,
        gfv: string,
        ignoreSizeCheck?: boolean
    ): Promise<FileProperties | undefined> {
        const response = await this.httpClient
            .get(`v1/files/prepare-and-get-properties`)
            .withQueryParams({ gfv: gfv, driveUri: driveUri, ignoreSizeCheck: ignoreSizeCheck ?? false })
            .acceptCode(413);
        if (response.status === 400) {
            return undefined;
        }
        if (response.status === 413) {
            const errorData = await response.json<{ errorCode: string; message: string }>();
            if (errorData.errorCode === "FufSizeTooLarge") {
                throw new FufSizeTooLargeError(errorData.message);
            }
            throw new HttpError(errorData.message, response.status);
        }
        return await response.json<FileProperties>();
    }

    public async uploadAndGetFileProperties(
        driveUris: string[],
        gfv: string,
        abortSignal?: AbortSignal
    ): Promise<UploadFilePropertiesResponse> {
        try {
            const response = await this.httpClient
                .post(`v1/files/upload-and-get-properties`)
                .withQueryParams({ gfv: gfv })
                .withJsonBody(driveUris)
                .withAbortSignal(abortSignal)
                .acceptCode(400)
                .acceptCode(413);
            if (response.status !== 200) {
                const data = await response.json<{ errorCode: ErrorCode; message: string }>();
                return { serverError: data.errorCode, invalidSourceDriveContentNames: "", sourcesTotalCount: 0 };
            }
            const data = await response.json<UploadFilePropertiesResponse>();
            return data;
        } catch {
            return {
                serverError: ErrorCode.UnknownError,
                invalidSourceDriveContentNames: "",
                sourcesTotalCount: 0,
            };
        }
    }

    public async acceptMultipleTransferType(
        files: ICombineByTransfers[],
        abortSignal?: AbortSignal
    ): Promise<ServerErrorResponse | undefined> {
        try {
            const response = await this.httpClient
                .post(`v1/ns/${this.draftFullId.ns}/drafts/${this.draftFullId.draftId}/transfer/combine-multiple`)
                .withJsonBody(files)
                .withAbortSignal(abortSignal)
                .acceptCode(400)
                .acceptCode(413);
            if (response.status !== 200) {
                const data = await response.json<ErrorResponse>();
                return { serverError: data.errorCode };
            }
            return undefined;
        } catch {
            return { serverError: ErrorCode.UnknownError };
        }
    }

    public async setFufByDriveUri(driveUri: string): Promise<void> {
        await this.httpClient.put(this.getDraftUrl("set-fuf")).withQueryParams({ driveUri: driveUri });
    }

    public async excelsToPartialFufs(
        gfv: string,
        pathToTable: string,
        driveUris: string[]
    ): Promise<ExcelsToPartialFufsResponse | undefined> {
        try {
            const response = await this.httpClient
                .post("v1/files/excels-to-partial-fufs")
                .withQueryParams({ gfv: gfv, pathToTable: pathToTable })
                .withJsonBody(driveUris);
            if (response.status === 200) {
                return await response.json<ExcelsToPartialFufsResponse>();
            }
            return undefined;
        } catch {
            return undefined;
        }
    }

    public async importXmls(
        driveUris: string[],
        combineMethod: FufCombineMethod,
        combinatorType: CombinatorType,
        isImportByFuf: boolean,
        args?: string[]
    ): Promise<CombineFufResult | undefined> {
        try {
            const response = await this.httpClient
                .post(this.getDraftUrl("transfer/import-xmls"))
                .withQueryParams({
                    combineMethod: combineMethod,
                    combinatorType: combinatorType,
                    importByFuf: isImportByFuf,
                })
                .withJsonBody({ FufDriveUris: driveUris, arguments: args });
            if (response.status === 400) {
                return undefined;
            }
            return await response.json<CombineFufResult>();
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
            return undefined;
        }
    }

    public async setDraftSettings(settings: DraftSettings): Promise<void> {
        await this.httpClient.patch(this.getDraftUrl("external/set-settings")).withJsonBody(settings);
    }

    public async getDraftSettings(): Promise<DraftSettings> {
        const response = await this.httpClient.get(this.getDraftUrl("external/get-settings"));

        return response.json<DraftSettings>();
    }

    public async normalizeByRules(rules: string): Promise<void> {
        await this.httpClient.post(this.getDraftUrl("test-normalize")).withJsonBody(rules);
    }

    private getHrefDownloadDraftPresentation(presentationType: DraftPresentationType): string {
        return this.httpClient.url(this.getDraftPresentationUrl(presentationType));
    }

    private getDraftUrl(draftRelatedUrl: string): string {
        return `v1/ns/${this.draftFullId.ns}/drafts/${this.draftFullId.draftId}/${draftRelatedUrl}`;
    }

    private getDraftPresentationUrl(presentationType: DraftPresentationType): string {
        return this.getDraftUrl(`presentations/${presentationType.toString()}`);
    }

    public async setMultipleWithNormalization(rules: ValueForInstancesSettingRule[]): Promise<IInnerChangeResult> {
        const uri = this.getDraftUrl("inner/set-multiple-with-normalization");
        const finalRules = rules.map(x => ({ ...x, path: convertToLegacyPath(x.path) }));
        const result = await this.httpClient.patch(uri).withJsonBody(finalRules);
        return result.status == 200 ? await result.json<IInnerChangeSuccessResult>() : result.status;
    }

    public async normalizeDirtyMasks(): Promise<IInnerChangeResult> {
        const uri = this.getDraftUrl("inner/normalize-dirty-masks");
        const result = await this.httpClient.post(uri);
        return result.status == 200 ? await result.json<IInnerChangeSuccessResult>() : result.status;
    }

    public async searchInstancesByText(
        text: string,
        pathsPrefix: string,
        pathsToSearch: string[],
        maxInstanceCount?: number
    ): Promise<string[]> {
        const maxInstanceCountParams = maxInstanceCount != undefined ? { maxInstanceCount: maxInstanceCount } : {};
        const params = { searchString: text, pathsPrefix: pathsPrefix, ...maxInstanceCountParams };
        return this.httpClient
            .post(this.getDraftUrl("inner/search-by-string"))
            .withQueryParams(params)
            .withJsonBody(pathsToSearch)
            .json<string[]>();
    }

    public async queryCollection(request: InnerQueryRequest, abortSignal?: AbortSignal): Promise<InnerQueryResponse> {
        return this.httpClient
            .post(this.getDraftUrl("inner/query-collection"))
            .withJsonBody(request)
            .withAbortSignal(abortSignal)
            .json<InnerQueryResponse>();
    }

    public async resolveInnerPath(
        request: ResolveInnerPathRequest,
        abortSignal?: AbortSignal
    ): Promise<ResolvePathResponse> {
        return this.httpClient
            .post(this.getDraftUrl("inner/resolve-inner-path"))
            .withJsonBody(request)
            .withAbortSignal(abortSignal)
            .json<ResolvePathResponse>();
    }

    public async customNormalize(
        normalizerId: string | null,
        pathPrefixes: AbsoluteModelPath[],
        abortSignal?: AbortSignal
    ): Promise<IInnerChangeResult> {
        const params = { normalizerId: normalizerId };
        const result = await this.httpClient
            .post(this.getDraftUrl("inner/custom-normalize"))
            .withQueryParams(params)
            .withJsonBody(pathPrefixes.map(x => x.toString()))
            .withAbortSignal(abortSignal);

        return result.status == 200 ? await result.json<IInnerChangeSuccessResult>() : result.status;
    }

    public async startCustomNormalizationTask(
        pathPrefixes: AbsoluteModelPath[],
        normalizerId?: string,
        operationName?: string
    ): Promise<PollingTaskWithResult<IInnerChangeResult>> {
        const params = { normalizerId: normalizerId, operationName: operationName ?? undefined };
        const args = { PathPrefixes: pathPrefixes.map(x => x.toString()) };
        const result = await this.httpClient
            .post(this.getDraftUrl("inner/custom-normalization-task"))
            .withQueryParams(params)
            .withJsonBody(args);
        return result.json<PollingTaskWithResult<IInnerChangeResult>>();
    }

    public async getCustomNormalizationTask(): Promise<PollingTaskWithResult<IInnerChangeResult>> {
        const result = await this.httpClient.get(this.getDraftUrl("inner/custom-normalization-task"));
        return result.json<PollingTaskWithResult<IInnerChangeResult>>();
    }

    public async importEmployees(request: ImportEmployeesRequest): Promise<void> {
        await this.httpClient.patch(this.getDraftUrl("staff")).withJsonBody(request);
    }

    public async deleteInstances(deletingRules: InstancesDeletingRule[]): Promise<void> {
        await this.httpClient.post(this.getDraftUrl("inner/delete-instances")).withJsonBody(deletingRules);
    }

    public async runMassAction(actionId: string, parameters?: MassActionParameters): Promise<void> {
        const queryParams = { actionId: actionId };
        await this.httpClient
            .post(this.getDraftUrl("inner/mass-action"))
            .withQueryParams(queryParams)
            .withJsonBody(parameters);
    }

    public print(type: PrintType, body?: string[]): Promise<PrintResponse> {
        return this.httpClient
            .post(this.getDraftUrl(`presentations/inner/print/${type}`))
            .withJsonBody(body)
            .json<PrintResponse>();
    }

    public async printExcel(pathToTable: string): Promise<PrintResponse | undefined> {
        try {
            const response = await this.httpClient
                .post(this.getDraftUrl(`presentations/table/print`))
                .withQueryParams({ pathToTable: pathToTable });
            if (response.status === 200) {
                const tableExcel = response.json<PrintResponse>();
                return tableExcel;
            }
            return undefined;
        } catch {
            return undefined;
        }
    }

    public async checkByPopd(): Promise<CandyPopdProtocol> {
        const response = await this.httpClient.get(
            `v1/ns/${this.draftFullId.ns}/drafts/${this.draftFullId.draftId}/checkByPopd`
        );

        return response.json<CandyPopdProtocol>();
    }

    public async getHistory(): Promise<InnerHistoryResponse | HttpStatusCode> {
        const url = this.getDraftUrl("innerhistory");
        const body = await this.httpClient
            .get(url)
            .acceptCode(400)
            .acceptCode(404)
            .acceptCode(500)
            .json<InnerHistoryResponse>();
        return body.map(item => ({
            ...item,
            DateUtc: convertDateFromStringIfNeeded(item.DateUtc),
            ChangesExpiresUtc: convertDateFromStringIfNeeded(item.ChangesExpiresUtc),
        }));
    }

    public async undoToChange(changeGUID: string): Promise<InnerHistoryUndoResponseWithStatus> {
        const url = this.getDraftUrl(`innerhistory/${changeGUID}`);
        const response = await this.httpClient.post(url).acceptCode(400).acceptCode(500);
        const innerHistoryUndoResponse = await response.json<InnerHistoryUndoResponse>();

        return {
            status: response.status,
            response: innerHistoryUndoResponse,
        };
    }

    public async getGFV(): Promise<string> {
        const url = `v1/ns/${this.draftFullId.ns}/drafts/${this.draftFullId.draftId}`;
        const body = await this.httpClient.get(url).json<GFVResponse>();
        const gfv = body.GFV;
        return `${gfv.Group}${gfv.Format}${gfv.Variant}`;
    }

    public async getUser(): Promise<GetUserResponse> {
        const url = `v1/user`;
        const body = await this.httpClient.get(url).json<GetUserResponse>();
        return body;
    }
}
